import React from 'react';
import { motion } from 'framer-motion';
import { FiImage, FiTrendingUp, FiUsers, FiShoppingBag, FiBarChart, FiAward, FiCheckCircle } from 'react-icons/fi';

const PinterestMarketingPage: React.FC = () => {
  const features = [
    {
      icon: FiImage,
      title: 'Visual Content Creation',
      description: 'Eye-catching pins designed to drive engagement and clicks with professional graphics and compelling descriptions.',
    },
    {
      icon: FiTrendingUp,
      title: 'Pinterest SEO',
      description: 'Optimize your pins and boards for Pinterest search to maximize organic reach and discovery.',
    },
    {
      icon: FiUsers,
      title: 'Audience Growth',
      description: 'Strategic follower growth campaigns to build an engaged community around your brand.',
    },
    {
      icon: FiShoppingBag,
      title: 'Shopping Pins',
      description: 'Leverage Pinterest shopping features to drive direct sales from your product pins.',
    },
    {
      icon: FiBarChart,
      title: 'Analytics & Reporting',
      description: 'Comprehensive performance tracking with detailed insights on pin performance and audience behavior.',
    },
    {
      icon: FiAward,
      title: 'Promoted Pins',
      description: 'Strategic Pinterest advertising campaigns to amplify your reach and drive conversions.',
    },
  ];

  const whatIsPinterestMarketing = [
    {
      title: 'Visual Discovery Engine',
      description: 'Pinterest is a unique platform where users actively search for inspiration, ideas, and products.',
    },
    {
      title: 'Long-Term Content Value',
      description: 'Pins have a longer lifespan than other social posts, continuing to drive traffic months after posting.',
    },
    {
      title: 'High Purchase Intent',
      description: 'Pinterest users are 7x more likely to say Pinterest is the most influential platform in their purchase journey.',
    },
    {
      title: 'E-commerce Powerhouse',
      description: 'Perfect for retail, fashion, food, home decor, and lifestyle brands looking to drive sales.',
    },
  ];

  const pricingPlans = [
    {
      name: 'Starter',
      price: '₹15,000',
      period: '/month',
      description: 'Perfect for businesses new to Pinterest',
      features: [
        '30 pins per month',
        '5 boards management',
        'Basic Pinterest SEO',
        'Monthly analytics report',
        'Pin design support',
        'Email support',
      ],
      highlighted: false,
    },
    {
      name: 'Professional',
      price: '₹30,000',
      period: '/month',
      description: 'For growing brands on Pinterest',
      features: [
        '60 pins per month',
        '10 boards management',
        'Advanced Pinterest SEO',
        'Shopping pins setup',
        'Weekly analytics reports',
        'Custom pin designs',
        'Promoted pins management (₹10K budget)',
        'Priority support',
      ],
      highlighted: true,
    },
    {
      name: 'Enterprise',
      price: '₹60,000',
      period: '/month',
      description: 'For serious Pinterest marketers',
      features: [
        '120+ pins per month',
        'Unlimited boards',
        'Complete Pinterest optimization',
        'Full shopping catalog integration',
        'Daily performance monitoring',
        'Professional design team',
        'Promoted pins strategy (₹30K+ budget)',
        'Influencer collaboration',
        'Dedicated account manager',
        '24/7 support',
      ],
      highlighted: false,
    },
  ];

  const whyChooseUs = [
    {
      title: 'Pinterest Certified Experts',
      description: 'Our team includes Pinterest-certified marketers who understand the platform\'s unique algorithm and best practices.',
    },
    {
      title: 'Proven Results',
      description: 'We\'ve helped brands achieve 300%+ growth in Pinterest traffic and consistently drive qualified leads.',
    },
    {
      title: 'E-commerce Focus',
      description: 'Specialized strategies for product-based businesses to maximize sales through Pinterest shopping features.',
    },
  ];

  const faqs = [
    {
      question: 'How long does it take to see results on Pinterest?',
      answer: 'Pinterest is a long-term strategy. You may start seeing initial traction within 2-3 months, with significant growth typically occurring after 6 months of consistent activity. However, pins continue to drive traffic for months or even years after posting.',
    },
    {
      question: 'Do I need a business account on Pinterest?',
      answer: 'Yes, a Pinterest Business account is essential for accessing analytics, advertising features, and claiming your website. We help you set up and optimize your business account as part of our service.',
    },
    {
      question: 'What types of businesses work best on Pinterest?',
      answer: 'Pinterest works exceptionally well for e-commerce, fashion, food, home decor, DIY, beauty, travel, and lifestyle brands. Any business with visually appealing products or services can succeed on Pinterest.',
    },
    {
      question: 'How do promoted pins work?',
      answer: 'Promoted pins are Pinterest\'s advertising format that amplifies your content to reach more people. They look like regular pins but appear in more places. We create and manage promoted pin campaigns optimized for your goals.',
    },
    {
      question: 'Can Pinterest really drive sales for my business?',
      answer: 'Absolutely! Pinterest users have high purchase intent - 89% of weekly Pinners use Pinterest for purchase inspiration. With proper optimization and shopping pins, Pinterest can become a significant sales channel.',
    },
    {
      question: 'What\'s the difference between a pin and a board?',
      answer: 'A pin is an individual piece of content (image, video, or product) you save to Pinterest. A board is a collection of pins organized around a specific theme or topic. We strategically organize your pins into optimized boards.',
    },
    {
      question: 'Do you create pin designs or do I need to provide them?',
      answer: 'We offer pin design services as part of our Professional and Enterprise packages. For the Starter package, we provide design templates and guidance, though you can also provide your own designs.',
    },
    {
      question: 'How do you measure Pinterest marketing success?',
      answer: 'We track key metrics including impressions, saves, clicks, profile visits, and website traffic from Pinterest. For e-commerce clients, we also monitor conversions and revenue attributed to Pinterest.',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-pink-50">
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#E60023]/5 via-transparent to-[#E60023]/5 z-0" />
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#E60023] to-[#bd081c] text-white px-6 py-2 rounded-full text-sm font-semibold mb-6">
              <FiImage className="w-4 h-4" />
              Pinterest Marketing Services
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-[#E60023] via-[#bd081c] to-[#E60023] bg-clip-text text-transparent leading-tight">
              Pinterest Marketing
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed break-words">
              Pinterest Marketing helps your brand get discovered through beautiful visuals, inspirational ideas, and keyword-based search behaviour. Pinterest works like a visual search engine, making it perfect for brands that want long-lasting visibility, consistent traffic, and high-intent customers.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-gradient-to-r from-[#E60023] to-[#bd081c] text-white px-8 py-4 rounded-xl font-semibold hover:shadow-xl transition-all duration-300"
              >
                Start Pinterest Marketing
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-[#E60023] px-8 py-4 rounded-xl font-semibold border-2 border-[#E60023] hover:bg-[#E60023] hover:text-white transition-all duration-300"
              >
                View Case Studies
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What is Pinterest Marketing Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4 text-gray-900 leading-tight break-words">
              What is Pinterest Marketing?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto break-words">
              Our pinterest social media marketing approach focuses on creating scroll-stopping pins, optimized boards, and smart posting routines that align with how users search on Pinterest. Whether you sell products, offer services, or share creative ideas, pinterest marketing helps your brand reach people who are already looking for something similar.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                title: 'Content Creation',
                description: 'High-quality pins, carousels, and creatives designed to match your style and grab user attention instantly.',
              },
              {
                title: 'Keyword & Board Optimization',
                description: 'Optimized boards, titles, and descriptions using Pinterest search trends to help your pins rank better.',
              },
              {
                title: 'Promoted Pins (Pinterest Ads)',
                description: 'Targeted Pinterest ads that reach users actively searching for ideas, products, or solutions like yours.',
              },
              {
                title: 'Growth & Performance Tracking',
                description: 'Monthly performance reports, insights, and strategy adjustments to keep your Pinterest growth steady and predictable.',
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gradient-to-br from-red-50 to-pink-50 p-6 rounded-2xl hover:shadow-lg transition-all duration-300"
              >
                <h3 className="text-xl font-semibold mb-3 text-gray-900 break-words">
                  {item.title}
                </h3>
                <p className="text-gray-600 break-words">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-red-50 to-pink-50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4 text-gray-900">
              Our Pinterest Marketing Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive Pinterest marketing solutions to maximize your visibility and drive conversions
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 group"
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-[#E60023] to-[#bd081c] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <IconComponent className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-gray-900 break-words">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 break-words">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4 text-gray-900">
              Pinterest Marketing Pricing
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Flexible plans designed to grow your Pinterest presence
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {pricingPlans.map((plan, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
                className={`relative p-8 rounded-2xl transition-all duration-300 ${
                  plan.highlighted
                    ? 'bg-gradient-to-br from-[#E60023] to-[#bd081c] text-white shadow-2xl scale-105'
                    : 'bg-white border-2 border-gray-200 shadow-lg hover:shadow-2xl'
                }`}
              >
                {plan.highlighted && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                    Most Popular
                  </div>
                )}
                <h3
                  className={`text-2xl font-bold mb-2 ${
                    plan.highlighted ? 'text-white' : 'text-gray-900'
                  }`}
                >
                  {plan.name}
                </h3>
                <div className="mb-4">
                  <span
                    className={`text-4xl font-bold ${
                      plan.highlighted ? 'text-white' : 'text-[#E60023]'
                    }`}
                  >
                    {plan.price}
                  </span>
                  <span
                    className={plan.highlighted ? 'text-red-100' : 'text-gray-600'}
                  >
                    {plan.period}
                  </span>
                </div>
                <p
                  className={`mb-6 ${
                    plan.highlighted ? 'text-red-100' : 'text-gray-600'
                  }`}
                >
                  {plan.description}
                </p>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start">
                      <FiCheckCircle
                        className={`w-5 h-5 mr-2 flex-shrink-0 mt-0.5 ${
                          plan.highlighted ? 'text-white' : 'text-[#E60023]'
                        }`}
                      />
                      <span
                        className={
                          plan.highlighted ? 'text-red-50' : 'text-gray-600'
                        }
                      >
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`w-full py-3 rounded-xl font-semibold transition-all duration-300 ${
                    plan.highlighted
                      ? 'bg-white text-[#E60023] hover:bg-red-50'
                      : 'bg-gradient-to-r from-[#E60023] to-[#bd081c] text-white hover:shadow-lg'
                  }`}
                >
                  Get Started
                </motion.button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-red-50 to-pink-50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4 text-gray-900">
              Why Choose Us for Pinterest Marketing?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              As a trusted pinterest marketing service provider, we keep your strategy simple, relatable, and results-driven. Our focus is on creating content people actually want to save, click, and share.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: 'Attractive Pins',
                description: 'Creating attractive pins your audience loves and setting up keyword-optimized boards for better visibility.',
              },
              {
                title: 'Trending Content',
                description: 'Following monthly trends and Pinterest search patterns while maintaining consistent posting and brand voice.',
              },
              {
                title: 'Long-Term Traffic',
                description: 'Bringing long-term organic traffic, not just short-term results. Our goal is to help your brand build traffic, engagement, and trust—without overwhelming your audience.',
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-[#E60023] bg-opacity-10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                    <FiCheckCircle className="w-5 h-5 text-[#E60023]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2 text-gray-900 break-words">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 break-words">{item.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4 text-gray-900">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to know about our Pinterest marketing services
            </p>
          </motion.div>
          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gradient-to-br from-red-50 to-pink-50 p-6 rounded-2xl"
              >
                <h3 className="text-lg font-semibold mb-2 text-gray-900 break-words">
                  {faq.question}
                </h3>
                <p className="text-gray-600 break-words">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-[#E60023] to-[#bd081c]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold mb-6 text-white">
              Ready to Grow Your Business on Pinterest?
            </h2>
            <p className="text-xl text-red-100 mb-8">
              Let's create a Pinterest marketing strategy that drives real results for your business
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-white text-[#E60023] px-8 py-4 rounded-xl font-semibold hover:bg-red-50 transition-all duration-300 shadow-xl"
            >
              Get Started Today
            </motion.button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PinterestMarketingPage;
